/* Trinity BioBlueprint — section controller (vanilla JS, no React).
 * Loads GSAP + ScrollTrigger + the helix bundle once, then initialises every
 * [data-bb-section] root. Each root gets its own gsap.context so it can be torn
 * down on shopify:section:unload / re-created on shopify:section:load without
 * duplicate listeners, triggers or animation loops. */
(function () {
  if (window.TrinityBB) return; // script tag rendered by several sections: run once
  var me = document.currentScript;
  var URLS = {
    gsap: me && me.getAttribute("data-gsap"),
    st: me && me.getAttribute("data-st"),
    helix: me && me.getAttribute("data-helix"),
  };
  var loads = {};
  function load(src) {
    if (!src) return Promise.reject(new Error("missing asset url"));
    if (loads[src]) return loads[src];
    loads[src] = new Promise(function (res, rej) {
      var s = document.createElement("script");
      s.src = src; s.async = false; s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });
    return loads[src];
  }
  var gsapReady = null;
  function needGsap() {
    if (!gsapReady) gsapReady = (window.gsap ? Promise.resolve() : load(URLS.gsap))
      .then(function () { return window.ScrollTrigger ? null : load(URLS.st); })
      .then(function () { window.gsap.registerPlugin(window.ScrollTrigger); });
    return gsapReady;
  }
  var helixReady = null;
  function needHelix() {
    if (!helixReady) helixReady = window.TrinityBBHelix ? Promise.resolve() : load(URLS.helix);
    return helixReady;
  }

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var instances = new Map();
  var refreshT = 0;
  function refresh() { clearTimeout(refreshT); refreshT = setTimeout(function () { window.ScrollTrigger && window.ScrollTrigger.refresh(); }, 120); }

  function q(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  // ---------------- per-section initialisers ----------------
  function commonReveal(gsap, root) {
    q(root, ".bb-reveal").forEach(function (el) {
      gsap.from(el, { y: 60, opacity: 0, duration: 1, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 85%" } });
    });
  }

  var INIT = {
    hero: function (gsap, root, cleanups) {
      gsap.from(q(root, ".bb-hero__copy > *"), { y: 40, opacity: 0, duration: 1.1, stagger: 0.12, ease: "power3.out" });
      var hero = root.querySelector(".bb-hero");
      gsap.to(root.querySelector(".bb-hero__copy"), { yPercent: -30, opacity: 0.2, ease: "none", scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: true } });
      var canvas = root.querySelector(".bb-canvas");
      if (canvas) gsap.to(canvas, { opacity: 0.28, ease: "none", scrollTrigger: { trigger: hero, start: "center top", end: "bottom top", scrub: true } });
    },
    home: function (gsap, root) {
      gsap.from(q(root, ".bb-hero__copy > *"), { y: 40, opacity: 0, duration: 1.1, stagger: 0.12, ease: "power3.out" });
    },
    signal: function (gsap, root) { commonReveal(gsap, root); },
    pillars: function (gsap, root, cleanups) {
      commonReveal(gsap, root);
      var cards = q(root, ".bb-card");
      function toggle(card, force) {
        var open = force != null ? force : card.getAttribute("data-open") !== "true";
        cards.forEach(function (c) {
          var on = c === card && open;
          c.setAttribute("data-open", on ? "true" : "false");
          var b = c.querySelector(".bb-card__plus"); b && b.setAttribute("aria-expanded", on ? "true" : "false");
        });
      }
      function onClick(e) {
        var card = e.target.closest(".bb-card");
        if (!card || !root.contains(card) || e.target.closest("a")) return;
        toggle(card);
      }
      function onKey(e) {
        if (e.key === "Escape") cards.forEach(function (c) { if (c.getAttribute("data-open") === "true") toggle(c, false); });
      }
      root.addEventListener("click", onClick);
      root.addEventListener("keydown", onKey);
      cleanups.push(function () { root.removeEventListener("click", onClick); root.removeEventListener("keydown", onKey); });
      root.__bbSelectBlock = function (el) { var c = el.closest(".bb-card") || el.querySelector(".bb-card"); c && toggle(c, true); };

      var grid = root.querySelector(".bb-pillars");
      if (!grid || reduced) return;
      var mm = gsap.matchMedia();
      cleanups.push(function () { mm.revert(); });
      mm.add("(min-width: 700px)", function () {
        var g = grid.getBoundingClientRect();
        var ox = window.innerWidth * 0.72;
        var tl = gsap.timeline({ scrollTrigger: { trigger: grid, start: "top 95%", end: "top 25%", scrub: 1 } });
        cards.forEach(function (c, i) {
          var r = c.getBoundingClientRect();
          tl.from(c, { x: ox - (r.left - g.left + g.left + r.width / 2), y: (i % 3) * -60 - 120, z: -600, rotateY: -50, rotateX: 20, scale: 0.35, opacity: 0, ease: "power2.out", duration: 1 }, i * 0.12);
        });
      });
      mm.add("(max-width: 699px)", function () {
        cards.forEach(function (c) {
          gsap.from(c, { y: 80, scale: 0.85, opacity: 0, rotateX: 25, duration: 0.9, scrollTrigger: { trigger: c, start: "top 90%" } });
        });
      });
    },
    report: function (gsap, root, cleanups) {
      commonReveal(gsap, root);
      var pages = q(root, ".bb-page");
      var tabs = q(root, ".bb-tab");
      var explain = root.querySelector("[data-bb-explain]");
      var state = { page: 0, explored: false };
      function fan() {
        if (!state.explored) return;
        pages.forEach(function (p, i) {
          var d = i - state.page, active = i === state.page;
          gsap.to(p, { x: active ? 0 : d * 34 + (d > 0 ? 60 : -60), y: active ? -10 : Math.abs(d) * 12, z: active ? 120 : -Math.abs(d) * 70,
            rotateX: active ? 4 : 14, rotateY: active ? -6 : d * -10, zIndex: 10 - Math.abs(d), duration: reduced ? 0 : 0.8, ease: "power3.out", overwrite: "auto" });
        });
      }
      function select(i) {
        state.page = i; state.explored = true;
        tabs.forEach(function (t, k) { t.setAttribute("aria-pressed", k === i ? "true" : "false"); });
        if (explain && pages[i]) explain.textContent = pages[i].getAttribute("data-explain") || "";
        fan();
      }
      gsap.fromTo(pages,
        { x: function (i) { return (i - 2) * 160; }, y: function (i) { return -220 + i * 40; }, z: function (i) { return -400 - i * 120; }, rotateX: 55, rotateZ: function (i) { return (i - 2) * 14; }, opacity: 0 },
        { x: function (i) { return i * 10; }, y: function (i) { return i * -10; }, z: function (i) { return -i * 40; }, rotateX: 18, rotateY: -22, rotateZ: 0, opacity: 1, stagger: 0.1, ease: "power2.out",
          scrollTrigger: { trigger: root.querySelector(".bb-stack"), start: "top 90%", end: "center 55%", scrub: 1, onLeave: function () { if (!state.explored) { state.explored = true; fan(); } } } });
      function onClick(e) {
        var t = e.target.closest(".bb-tab, .bb-page");
        if (!t || !root.contains(t)) return;
        var i = Number(t.getAttribute("data-index"));
        if (!isNaN(i)) select(i);
      }
      function onKey(e) {
        if ((e.key === "Enter" || e.key === " ") && e.target.classList && e.target.classList.contains("bb-page")) { e.preventDefault(); onClick(e); }
      }
      root.addEventListener("click", onClick);
      root.addEventListener("keydown", onKey);
      cleanups.push(function () { root.removeEventListener("click", onClick); root.removeEventListener("keydown", onKey); });
      root.__bbSelectBlock = function (el) { var i = Number(el.getAttribute("data-index")); if (!isNaN(i)) select(i); };
    },
    process: function (gsap, root) {
      commonReveal(gsap, root);
      q(root, "[data-to]").forEach(function (el) {
        var to = Number(el.getAttribute("data-to")), o = { v: 0 };
        gsap.to(o, { v: to, duration: reduced ? 0 : 1.8, ease: "power2.out", scrollTrigger: { trigger: el, start: "top 90%" }, onUpdate: function () { el.textContent = Math.round(o.v).toString(); } });
      });
    },
    kits: function (gsap, root, cleanups) {
      commonReveal(gsap, root);
      q(root, ".bb-parallax").forEach(function (el) {
        gsap.to(el, { yPercent: -12, ease: "none", scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: true } });
      });
      if (reduced) return;
      function move(e) {
        var el = e.target.closest(".bb-product"); if (!el || !root.contains(el)) return;
        var r = el.getBoundingClientRect(), x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = "perspective(1000px) rotateY(" + x * 10 + "deg) rotateX(" + -y * 10 + "deg)";
      }
      function leave(e) { var el = e.target.closest && e.target.closest(".bb-product"); if (el && !el.contains(e.relatedTarget)) el.style.transform = ""; }
      root.addEventListener("mousemove", move);
      root.addEventListener("mouseout", leave);
      cleanups.push(function () { root.removeEventListener("mousemove", move); root.removeEventListener("mouseout", leave); });
    },
    billing: function (gsap, root) { commonReveal(gsap, root); },
  };

  // Clinic billing form: client-side checks + loading state only. The form itself
  // posts natively to Shopify's /contact endpoint; success is rendered by Liquid
  // (form.posted_successfully?) after Shopify accepts it — never by this script.
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, PHONE_RE = /^[+()\d\s.-]{7,40}$/;
  function bindBilling(root, cleanups) {
    var form = root.querySelector("form.bb-billing__card");
    if (!form) return;
    form.noValidate = true;
    function unlock() { var b = form.querySelector("button[type=submit]"); if (b) { b.disabled = false; b.removeAttribute("aria-busy"); var l = b.querySelector("[data-label]"); if (l && l.__t) l.textContent = l.__t; } }
    window.addEventListener("pageshow", unlock);
    cleanups.push(function () { window.removeEventListener("pageshow", unlock); });
    function msg(input) {
      var v = (input.value || "").trim(), req = input.getAttribute("data-msg-required") || "This field is required.";
      if (!v) return req;
      if (input.type === "email" && !EMAIL_RE.test(v)) return input.getAttribute("data-msg-invalid") || "Please enter a valid email address.";
      if (input.type === "tel" && !PHONE_RE.test(v)) return input.getAttribute("data-msg-invalid") || "Please enter a valid phone number.";
      if (input.type === "number" && (!/^\d+$/.test(v) || +v < 1 || +v > 100000)) return input.getAttribute("data-msg-invalid") || "Please enter a whole number of 1 or more.";
      return "";
    }
    function show(input, m) {
      var err = form.querySelector('[data-err-for="' + input.id + '"]');
      input.setAttribute("aria-invalid", m ? "true" : "false");
      if (err) { err.textContent = m; err.hidden = !m; }
    }
    function onSubmit(e) {
      var first = null;
      q(form, "input[data-bb-field]").forEach(function (i) { var m = msg(i); show(i, m); if (m && !first) first = i; });
      if (first) { e.preventDefault(); first.focus(); return; }
      var btn = form.querySelector("button[type=submit]");
      if (btn) { btn.disabled = true; btn.setAttribute("aria-busy", "true"); var l = btn.querySelector("[data-label]"); if (l) { l.__t = l.textContent; } if (l) l.textContent = btn.getAttribute("data-loading") || "Sending…"; }
    }
    function onInput(e) { var i = e.target; if (i.matches && i.matches("input[data-bb-field]") && i.getAttribute("aria-invalid") === "true") show(i, msg(i)); }
    form.addEventListener("submit", onSubmit);
    form.addEventListener("input", onInput);
    cleanups.push(function () { form.removeEventListener("submit", onSubmit); form.removeEventListener("input", onInput); });
  }

  function mountHelix(root, cleanups) {
    var el = root.querySelector("[data-bb-helix]");
    if (!el) return;
    var mode = el.getAttribute("data-bb-helix"); // "page" (fixed, scroll-linked) | "home"
    if (mode === "page") {
      // only one fixed, page-wide helix may run at a time
      var owner = document.querySelector("[data-bb-helix-owner]");
      if (owner && owner !== el) { el.style.display = "none"; return; }
      el.setAttribute("data-bb-helix-owner", "");
      cleanups.push(function () { el.removeAttribute("data-bb-helix-owner"); });
    }
    var api = null, dead = false;
    needHelix().then(function () {
      if (dead) return;
      api = window.TrinityBBHelix.mount(el, { scroll: mode === "page" });
    }).catch(function (err) { el.setAttribute("data-scene-fallback", "true"); console.warn("[TrinityBB] helix failed", err); });
    cleanups.push(function () { dead = true; api && api.destroy(); });
  }

  function init(root) {
    if (instances.has(root)) return;
    var type = root.getAttribute("data-bb-section");
    var cleanups = [];
    var inst = { ctx: null, cleanups: cleanups, dead: false };
    instances.set(root, inst);
    mountHelix(root, cleanups);
    if (type === "billing") bindBilling(root, cleanups);
    needGsap().then(function () {
      if (inst.dead || !INIT[type]) return;
      var gsap = window.gsap;
      inst.ctx = gsap.context(function () { INIT[type](gsap, root, cleanups); }, root);
      refresh();
    }).catch(function (err) { root.classList.add("bb-no-motion"); console.warn("[TrinityBB] GSAP failed to load", err); });
  }
  function destroy(root) {
    var inst = instances.get(root);
    if (!inst) return;
    inst.dead = true;
    inst.cleanups.splice(0).reverse().forEach(function (f) { try { f(); } catch (e) {} });
    inst.ctx && inst.ctx.revert();
    delete root.__bbSelectBlock;
    instances.delete(root);
    refresh();
  }
  function scan(scope) { q(scope || document, "[data-bb-section]").forEach(init); }
  function teardown(scope) { q(scope, "[data-bb-section]").forEach(destroy); }

  // ---------------- Shopify theme editor lifecycle ----------------
  document.addEventListener("shopify:section:load", function (e) { scan(e.target); });
  document.addEventListener("shopify:section:unload", function (e) { teardown(e.target); });
  document.addEventListener("shopify:block:select", function (e) {
    var root = e.target.closest("[data-bb-section]");
    root && root.__bbSelectBlock && root.__bbSelectBlock(e.target);
  });
  document.addEventListener("shopify:section:reorder", refresh);

  window.TrinityBB = { init: init, destroy: destroy, scan: scan, instances: instances };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { scan(); });
  else scan();
})();
